import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useFetchPreflightData } from "./useFetchPreflightData";
import { useOAuthPopupConnect } from "./oauthPopup";

/**
 * useOAuthConnection
 *
 * Owns the OAuth protocol lifecycle shared by every framework-OAuth
 * integration (Slack, Typeform, Discord, Fathom, Notion, ...). Each
 * integration writes its own JSX; this hook only owns state and the
 * provider-agnostic network calls:
 *
 *   - GET    /api/externalConnections/{providerId}/status
 *   - POST   /api/externalConnections/{providerId}/connect
 *            (OAuth: popup + redirectUrl; apiToken providers such as Kobo:
 *             connectWithToken posts the pasted token directly, no popup)
 *   - POST   /api/externalConnections/{providerId}/disconnect
 *   - Detects ?externalConnectionResult=...&provider=...&reason=... on mount
 *     and refetches status, then cleans the URL.
 *   - Lazy-loads preflight data once a non-stale connection exists.
 *
 * What it DOESN'T own (deliberate):
 *   - The picker's selected value (each integration's shape differs).
 *   - Date ranges / other secondary form state (provider-specific).
 *   - Layout, copy, banners — the integration's component owns all JSX.
 *
 * Layout-sharing without a base component is intentional: every layout
 * decision (picker shape, extra controls, where the hint line sits, what
 * the disconnect button says) varies enough across providers that capturing
 * it in a shared component accretes config-flag knobs over time. State and
 * the OAuth protocol are universal — those are safe to share.
 *
 * @param {Object} params
 * @param {string} params.providerId        e.g. "notion" — drives every URL
 * @param {string} params.providerName      e.g. "Notion" — used only in default error copy
 * @param {boolean} [params.preflightEnabled=true]  set false for providers with no picker (Fathom)
 *
 * @returns {{
 *   connection: Object|null,
 *   connectionLoading: boolean,
 *   connectError: string|null,
 *   setConnectError: Function,
 *   preflightData: Object|null,
 *   preflightError: string|null,
 *   isPreflightLoading: boolean,
 *   handleConnect: Function,
 *   connectWithToken: Function,
 *   isConnectingWithToken: boolean,
 *   handleDisconnect: Function,
 *   reloadStatus: Function,
 * }}
 */
export const useOAuthConnection = ({
  providerId,
  providerName,
  preflightEnabled = true,
}) => {
  const [connection, setConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectError, setConnectError] = useState(null);
  const [preflightData, setPreflightData] = useState(null);
  const [preflightError, setPreflightError] = useState(null);
  // Separate from the popup flow's isConnecting: the two never run together.
  const [isConnectingWithToken, setIsConnectingWithToken] = useState(false);
  // Guards the lazy-preflight effect below; see the comment there.
  const preflightAttemptedRef = useRef(false);

  const {
    mutate: fetchPreflight,
    isLoading: isPreflightLoading,
  } = useFetchPreflightData();

  const reloadStatus = useCallback(async () => {
    setConnectionLoading(true);
    try {
      const { data } = await axios.get(
        `/api/externalConnections/${providerId}/status`
      );
      setConnection(data);
    } catch (err) {
      setConnectError(
        err.response?.data?.error ||
          err.message ||
          `Failed to read ${providerName} connection status`
      );
    } finally {
      setConnectionLoading(false);
    }
  }, [providerId, providerName]);

  useEffect(() => {
    reloadStatus();
  }, [reloadStatus]);

  // OAuth return handling — refetch status, surface any error reason, clean URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("externalConnectionResult");
    const provider = params.get("provider");
    if (result && provider === providerId) {
      if (result === "error") {
        const reason = params.get("reason") || "unknown";
        setConnectError(
          `${providerName} connection failed: ${reason}. Please try again.`
        );
      }
      reloadStatus();
      params.delete("externalConnectionResult");
      params.delete("provider");
      params.delete("reason");
      const qs = params.toString();
      const newUrl =
        window.location.pathname +
        (qs ? `?${qs}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, [reloadStatus, providerId, providerName]);

  // Popup OAuth return: the shared hook owns the popup + the postMessage
  // listener and calls us back with the result (the full-page redirect fallback
  // is handled by the URL-param effect above).
  const { startConnect, isConnecting } = useOAuthPopupConnect({
    providerId,
    providerName,
    onResult: ({ result, reason }) => {
      if (result === "error") {
        setConnectError(
          `${providerName} connection failed: ${reason || "unknown"}. Please try again.`
        );
      }
      reloadStatus();
    },
  });

  // Lazy preflight: only after we know the connection is active, and at most
  // once per connection state. Without the attempted-ref the effect loops: its
  // onError calls reloadStatus(), which stores a NEW connection object, which
  // re-runs the effect, which fails again — several times a second for as long
  // as the provider stays unreachable (a 401 is the only failure that stops it
  // on its own, by flipping the row to stale). Reset by connectWithToken /
  // handleDisconnect, which are the actions that can change the outcome.
  useEffect(() => {
    if (!preflightEnabled) return;
    if (!connection?.connected || connection?.status === "stale") return;
    if (preflightData !== null) return;
    if (preflightAttemptedRef.current) return;
    preflightAttemptedRef.current = true;
    setPreflightError(null);
    fetchPreflight(
      { sourceId: providerId },
      {
        onSuccess: (data) => {
          setPreflightData(data.preflightData || {});
        },
        onError: (err) => {
          setPreflightError(
            err.message || `Failed to load ${providerName} data.`
          );
          // A 401 here will have marked the connection stale server-side;
          // refresh status so the UI flips to the Reconnect banner.
          reloadStatus();
        },
      }
    );
  }, [
    preflightEnabled,
    connection,
    preflightData,
    fetchPreflight,
    reloadStatus,
    providerId,
    providerName,
  ]);

  /**
   * Initiate the OAuth handshake. Accepts an optional `{ providerParams }`
   * object that's forwarded to the backend connect endpoint and round-tripped
   * through the OAuth state record (Redis, 10min). Used by providers that
   * need to know something *before* the redirect — e.g., Zoho Desk needs the
   * region (US/EU/AU/...) to pick the right DC OAuth host.
   *
   * Existing callers that pass no argument keep working unchanged.
   */
  const handleConnect = useCallback(async ({ providerParams } = {}) => {
    setConnectError(null);
    try {
      await startConnect({
        body: providerParams ? { providerParams } : undefined,
      });
    } catch (err) {
      setConnectError(
        err.response?.data?.error ||
          err.message ||
          `Could not start ${providerName} connection`
      );
    }
  }, [startConnect, providerName]);

  /**
   * Token-provider connect (KoboToolbox). Posts the pasted token straight to
   * /connect (no popup, no redirect), then resets preflight so the lazy
   * preflight effect refires (a stale -> active reconnect must reload the
   * form list) and refetches status. Resolves true on success; on failure
   * sets connectError from the BE's { errors: [{ msg }] } shape and resolves
   * false. The token is never kept in this hook's state.
   */
  const connectWithToken = useCallback(async ({ apiToken, providerParams } = {}) => {
    setConnectError(null);
    setIsConnectingWithToken(true);
    try {
      await axios.post(`/api/externalConnections/${providerId}/connect`, {
        apiToken,
        ...(providerParams ? { providerParams } : {}),
      });
      setPreflightData(null);
      setPreflightError(null);
      preflightAttemptedRef.current = false;
      await reloadStatus();
      return true;
    } catch (err) {
      setConnectError(
        err.response?.data?.errors?.[0]?.msg ||
          err.response?.data?.error ||
          err.message ||
          `Could not connect ${providerName}`
      );
      return false;
    } finally {
      setIsConnectingWithToken(false);
    }
  }, [providerId, providerName, reloadStatus]);

  const handleDisconnect = useCallback(async () => {
    setConnectError(null);
    try {
      await axios.post(`/api/externalConnections/${providerId}/disconnect`);
      setConnection({ connected: false });
      setPreflightData(null);
      setPreflightError(null);
      preflightAttemptedRef.current = false;
    } catch (err) {
      setConnectError(
        err.response?.data?.error || err.message || "Disconnect failed"
      );
    }
  }, [providerId]);

  return {
    connection,
    connectionLoading,
    connectError,
    setConnectError,
    preflightData,
    preflightError,
    isPreflightLoading,
    handleConnect,
    isConnecting,
    connectWithToken,
    isConnectingWithToken,
    handleDisconnect,
    reloadStatus,
  };
};

export default useOAuthConnection;
